import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, ToggleLeftIcon, ToggleRightIcon, ArrowUpIcon, ArrowDownIcon, FilterIcon, XIcon, TagIcon } from 'lucide-react';
import { Tag } from './TagsManagement';
import { StatusBadge } from '../../StatusBadge';
type SortField = 'name' | 'tagType' | 'active';
type SortDirection = 'asc' | 'desc';
interface TagsListProps {
  globalTags: Tag[];
  orgUnitTags: Tag[];
  onAdd: () => void;
  onEdit: (tag: Tag) => void;
  onToggleActive: (id: string) => void;
  onDelete: (id: string) => void;
  locale: string;
}
export function TagsList({
  globalTags,
  orgUnitTags,
  onAdd,
  onEdit,
  onToggleActive,
  onDelete,
  locale
}: TagsListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    tagType: '',
    active: ''
  });
  const allTags = [...globalTags, ...orgUnitTags];
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  const resetFilters = () => {
    setFilters({
      tagType: '',
      active: ''
    });
    setSearchTerm('');
  };
  // Get unique tag types for filters
  const uniqueTagTypes = Array.from(new Set(allTags.filter(tag => tag.tagTypeName).map(tag => tag.tagTypeName)));
  // Apply filters and sorting to a tag list
  const filterAndSortTags = (tags: Tag[]) => {
    const filtered = tags.filter(tag => {
      // Search term filter
      const searchMatch = tag.name.toLowerCase().includes(searchTerm.toLowerCase()) || tag.description && tag.description.toLowerCase().includes(searchTerm.toLowerCase()) || tag.tagTypeName && tag.tagTypeName.toLowerCase().includes(searchTerm.toLowerCase());
      // Tag type filter
      const tagTypeMatch = !filters.tagType || tag.tagTypeName === filters.tagType;
      // Active status filter
      const activeMatch = filters.active === '' || filters.active === 'active' && tag.isActive || filters.active === 'inactive' && !tag.isActive;
      return searchMatch && tagTypeMatch && activeMatch;
    });
    // Apply sorting
    return [...filtered].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'tagType':
          const tagTypeA = a.tagTypeName || '';
          const tagTypeB = b.tagTypeName || '';
          comparison = tagTypeA.localeCompare(tagTypeB);
          break;
        case 'active':
          comparison = Number(a.isActive) - Number(b.isActive);
          break;
        default:
          comparison = 0;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };
  const filteredGlobalTags = filterAndSortTags(globalTags);
  const filteredOrgUnitTags = filterAndSortTags(orgUnitTags);
  // Render tag with its color
  const renderTagBadge = (tag: Tag) => {
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{
      backgroundColor: `${tag.colourHex}20`,
      color: tag.colourHex,
      border: `1px solid ${tag.colourHex}`
    }} data-id="element-20177">
        <TagIcon className="w-3 h-3 mr-1" data-id="element-20178" />
        {tag.name}
      </span>;
  };
  const renderTagTable = (tags: Tag[], showOrgUnit: boolean = false) => {
    return <table className="min-w-full divide-y divide-gray-200" data-id="element-20179">
        <thead className="bg-gray-50" data-id="element-20180">
          <tr data-id="element-20181">
            {showOrgUnit && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20182">
                Organisation Unit
              </th>}
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('name')} data-id="element-20183">
              <div className="flex items-center space-x-1" data-id="element-20184">
                <span data-id="element-20185">Tag</span>
                {sortField === 'name' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-20186" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-20187" />)}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('tagType')} data-id="element-20188">
              <div className="flex items-center space-x-1" data-id="element-20189">
                <span data-id="element-20190">Tag Type</span>
                {sortField === 'tagType' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-20191" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-20192" />)}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20193">
              Date Range
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('active')} data-id="element-20194">
              <div className="flex items-center space-x-1" data-id="element-20195">
                <span data-id="element-20196">Status</span>
                {sortField === 'active' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-20197" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-20198" />)}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20199">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-20200">
          {tags.length === 0 ? <tr data-id="element-20201">
              <td colSpan={showOrgUnit ? 6 : 5} className="px-6 py-12 text-center text-sm text-gray-500" data-id="element-20202">
                No tags found.
              </td>
            </tr> : tags.map(tag => <tr key={tag.id} className="hover:bg-gray-50" data-id="element-20203">
                {showOrgUnit && <td className="px-6 py-4" data-id="element-20204">
                    <div className="text-sm font-medium text-gray-900" data-id="element-20205">
                      {tag.orgUnitName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-20206">
                      {tag.orgUnitPath}
                    </div>
                  </td>}
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-20207">
                  <div className="flex flex-col" data-id="element-20208">
                    <div className="mb-1" data-id="element-20209">{renderTagBadge(tag)}</div>
                    {tag.description && <span className="text-xs text-gray-500 truncate max-w-xs" data-id="element-20210">
                        {tag.description}
                      </span>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-20211">
                  {tag.tagTypeName || <span className="text-gray-400 italic" data-id="element-20212">None</span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-20213">
                  {tag.activeFrom ? <span data-id="element-20214">
                      {new Date(tag.activeFrom).toLocaleDateString(locale)}
                      {tag.activeTo ? <span data-id="element-20215">
                          {' '}
                          to {new Date(tag.activeTo).toLocaleDateString(locale)}
                        </span> : <span className="text-gray-400 italic" data-id="element-20216"> - Ongoing</span>}
                    </span> : <span className="text-gray-400 italic" data-id="element-20217">
                      No date restriction
                    </span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-20218">
                  <StatusBadge status={tag.isActive ? 'success' : 'error'} text={tag.isActive ? 'Active' : 'Inactive'} data-id="element-20219" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-20220">
                  <div className="flex justify-end space-x-2" data-id="element-20221">
                    <button onClick={() => onEdit(tag)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-20222">
                      <EditIcon className="h-4 w-4" data-id="element-20223" />
                    </button>
                    <button onClick={() => onToggleActive(tag.id)} className={`${tag.isActive ? 'text-green-600 hover:text-green-900' : 'text-gray-400 hover:text-gray-600'}`} title={tag.isActive ? 'Deactivate' : 'Activate'} data-id="element-20224">
                      {tag.isActive ? <ToggleRightIcon className="h-4 w-4" data-id="element-20225" /> : <ToggleLeftIcon className="h-4 w-4" data-id="element-20226" />}
                    </button>
                    <button onClick={() => onDelete(tag.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-20227">
                      <TrashIcon className="h-4 w-4" data-id="element-20228" />
                    </button>
                  </div>
                </td>
              </tr>)}
        </tbody>
      </table>;
  };
  return <div className="space-y-6" data-id="element-20229">
      {/* Search and Filters */}
      <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-20230">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-20231">
          <div className="flex-1 max-w-md" data-id="element-20232">
            <div className="relative rounded-md shadow-sm" data-id="element-20233">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-20234">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-20235" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search tags..." data-id="element-20236" />
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-20237">
            <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20238">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-20239" />
              Filters
              {Object.values(filters).some(f => f !== '') && <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-20240">
                  {Object.values(filters).filter(f => f !== '').length}
                </span>}
            </button>
            <button onClick={onAdd} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20241">
              <PlusIcon className="h-4 w-4 mr-2" data-id="element-20242" />
              Add Tag
            </button>
          </div>
        </div>

        {/* Filter chips */}
        {Object.values(filters).some(f => f !== '') && <div className="mt-4 flex flex-wrap gap-2" data-id="element-20243">
            {filters.tagType && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-20244">
                Type: {filters.tagType}
                <button type="button" onClick={() => setFilters({
            ...filters,
            tagType: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none" data-id="element-20245">
                  <XIcon className="h-3 w-3" data-id="element-20246" />
                </button>
              </span>}
            {filters.active && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-20247">
                Status: {filters.active === 'active' ? 'Active' : 'Inactive'}
                <button type="button" onClick={() => setFilters({
            ...filters,
            active: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none" data-id="element-20248">
                  <XIcon className="h-3 w-3" data-id="element-20249" />
                </button>
              </span>}
            <button type="button" onClick={resetFilters} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20250">
              Clear All
            </button>
          </div>}

        {/* Expanded filters */}
        {showFilters && <div className="mt-4 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2" data-id="element-20251">
            <div data-id="element-20252">
              <label htmlFor="tagType" className="block text-sm font-medium text-gray-700" data-id="element-20253">
                Tag Type
              </label>
              <select id="tagType" name="tagType" value={filters.tagType} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-20254">
                <option value="" data-id="element-20255">All Types</option>
                <option value={null} data-id="element-20256">No Type</option>
                {uniqueTagTypes.map(tagType => <option key={tagType} value={tagType} data-id="element-20257">
                    {tagType}
                  </option>)}
              </select>
            </div>
            <div data-id="element-20258">
              <label htmlFor="active" className="block text-sm font-medium text-gray-700" data-id="element-20259">
                Status
              </label>
              <select id="active" name="active" value={filters.active} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-20260">
                <option value="" data-id="element-20261">All Statuses</option>
                <option value="active" data-id="element-20262">Active</option>
                <option value="inactive" data-id="element-20263">Inactive</option>
              </select>
            </div>
          </div>}
      </div>

      {/* Global Tags */}
      <div className="bg-white shadow rounded-lg" data-id="element-20264">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-20265">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-20266">
            Global Tags
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-20267">
            Global tags available to all organisation units
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-20268">
          {renderTagTable(filteredGlobalTags, false)}
        </div>
      </div>

      {/* OrgUnit Tags */}
      <div className="bg-white shadow rounded-lg" data-id="element-20269">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-20270">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-20271">
            Organisation Unit Tags
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-20272">
            Tags specific to individual organisation units
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-20273">
          {renderTagTable(filteredOrgUnitTags, true)}
        </div>
      </div>
    </div>;
}